// PersistentLiveRoom — Host de la sala de video. Se monta en el Layout
// (fuera del <Outlet/>), por lo que SOBREVIVE a los cambios de ruta: la clase
// sigue activa en segundo plano. Cuando estás en la página de la sala,
// el video se "acopla" al hueco reservado (dockEl). Al navegar a otro módulo,
// se convierte en una mini-ventana flotante con controles para volver o
// finalizar. Escucha `class_ended` para desconectar a todos.
//
// v5.1.0: Motor de video Agora RTC Web SDK.
import { memo, useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Loader2, Radio, Maximize2, PhoneOff, LogOut } from "lucide-react";
import { toast } from "sonner";
import { api, getAccessToken } from "@/lib/api";
import { useLiveSession } from "@/contexts/LiveSessionContext";
import AgoraRoom from "./AgoraRoom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const WS_BASE = BACKEND_URL.replace(/^http/, "ws");

export default function PersistentLiveRoom() {
  const { session, closeSession, dockEl } = useLiveSession();
  const location = useLocation();
  const navigate = useNavigate();
  const [rect, setRect] = useState(null);
  const wsRef = useRef(null);

  const classId = session?.classId;
  const data = session?.data;
  const isModerator = !!data?.is_moderator;
  const isOnClassRoute = !!(session && location.pathname.includes(`/clases-en-vivo/${classId}/sala`));

  const leaveAndClose = useCallback(() => {
    const wasOnRoute = location.pathname.includes(`/clases-en-vivo/${classId}/sala`);
    closeSession();
    if (wasOnRoute && session?.roomPath) {
      navigate(session.roomPath.replace(/\/[^/]+\/sala$/, ""));
    } else if (wasOnRoute) {
      navigate(-1);
    }
  }, [classId, location.pathname, session, navigate, closeSession]);

  // Seguir la posición del hueco acoplado (dockEl) mientras estás en la página.
  useEffect(() => {
    if (!(isOnClassRoute && dockEl)) { setRect(null); return; }
    let raf;
    const tick = () => {
      const r = dockEl.getBoundingClientRect();
      setRect((prev) => {
        if (prev && Math.abs(prev.top - r.top) < 0.5 && Math.abs(prev.left - r.left) < 0.5
            && Math.abs(prev.width - r.width) < 0.5 && Math.abs(prev.height - r.height) < 0.5) {
          return prev;
        }
        return { top: r.top, left: r.left, width: r.width, height: r.height };
      });
      raf = requestAnimationFrame(tick);
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [isOnClassRoute, dockEl]);

  // WS: escuchar `class_ended` para desconectar a los estudiantes (Bug 1).
  useEffect(() => {
    if (!classId) return;
    const token = getAccessToken();
    if (!token) return;
    let ws;
    try {
      ws = new WebSocket(`${WS_BASE}/api/ws/whiteboard/${classId}?token=${encodeURIComponent(token)}`);
      wsRef.current = ws;
      ws.onmessage = (ev) => {
        let msg; try { msg = JSON.parse(ev.data); } catch { return; }
        if (msg.type === "class_ended" && !isModerator) {
          toast.info("El docente finalizó la clase.");
          leaveAndClose();
        }
      };
    } catch { /* ws no disponible */ }
    return () => { try { ws && ws.close(); } catch { /* ya cerrado */ } };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, isModerator]);

  // Respaldo REST: si el WS está bloqueado en producción, sondeamos el estado.
  useEffect(() => {
    if (!classId || isModerator) return;
    const iv = setInterval(async () => {
      try {
        const { data: c } = await api.get(`/live-classes/${classId}`);
        if (c?.live_session_ended || c?.status === "cancelled") {
          toast.info("El docente finalizó la clase.");
          leaveAndClose();
        }
      } catch { /* ignore */ }
    }, 8000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [classId, isModerator]);

  const endForEveryone = async () => {
    if (!window.confirm("¿Finalizar la clase para todos? Se desconectará a los estudiantes.")) return;
    try {
      await api.post(`/live-classes/${classId}/end`);
      toast.success("Clase finalizada.");
      leaveAndClose();
    } catch {
      toast.error("No se pudo finalizar la clase. Intenta de nuevo.");
    }
  };

  if (!session) return null;

  const docked = isOnClassRoute && rect && rect.width > 4 && rect.height > 4;
  // En "solo pizarra" el hueco queda oculto (rect nulo/0) pero mantenemos el
  // video vivo fuera de pantalla (audio sigue) sin mostrarlo.
  // v5.7.3 · si la página de la sala aún no registró su hueco (cargando), el video queda en la
  // mini-ventana visible en vez de oculto fuera de pantalla.
  const hiddenDock = isOnClassRoute && !!dockEl && (!rect || rect.width <= 4 || rect.height <= 4);

  let wrapStyle;
  if (docked) {
    wrapStyle = {
      position: "fixed", top: rect.top, left: rect.left, width: rect.width, height: rect.height,
      // v5.7.1 · cuando el video es FLOTANTE sobre la pizarra va por encima de ella (antes quedaba tapado)
      zIndex: dockEl?.dataset?.floating === "1" ? 45 : 20, borderRadius: 16, overflow: "hidden", background: "#000",
      ...(dockEl?.dataset?.floating === "1" ? { boxShadow: "0 18px 50px rgba(31,37,71,.45)", border: "2px solid #A43790" } : {}),
    };
  } else if (hiddenDock) {
    wrapStyle = {
      position: "fixed", top: -99999, left: -99999, width: 320, height: 200,
      overflow: "hidden", background: "#000",
    };
  } else {
    // Flotante (navegaste a otro módulo): mini-ventana abajo a la derecha.
    wrapStyle = {
      position: "fixed", bottom: 16, right: 16, width: 340, height: 232,
      zIndex: 60, borderRadius: 16, overflow: "hidden", background: "#000",
      boxShadow: "0 18px 50px rgba(31,37,71,.45)", border: "2px solid #A43790",
    };
  }

  const floating = !docked && !hiddenDock;

  return (
    <div style={wrapStyle} data-testid="persistent-live-room" data-mode={docked ? "docked" : hiddenDock ? "hidden" : "floating"}>
      {floating && (
        <div className="absolute top-0 inset-x-0 z-10 flex items-center justify-between gap-2 px-2.5 py-1.5 bg-[#1F2547]/90 backdrop-blur">
          <span className="text-[11px] font-extrabold text-white inline-flex items-center gap-1.5 min-w-0">
            <Radio size={12} className="text-[#F5B82E] animate-pulse shrink-0" />
            <span className="truncate">{session.data?.title || "Clase en vivo"}</span>
          </span>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => navigate(session.roomPath || -1)}
              data-testid="floating-return-btn"
              title="Volver a la clase"
              className="text-white bg-[#A43790] hover:bg-[#80286D] rounded-full p-1.5"
            >
              <Maximize2 size={13} />
            </button>
            {isModerator ? (
              <button
                type="button"
                onClick={endForEveryone}
                data-testid="floating-end-btn"
                title="Finalizar la clase para todos"
                className="text-white bg-[#A52258] hover:bg-[#8A1C49] rounded-full p-1.5"
              >
                <PhoneOff size={13} />
              </button>
            ) : (
              <button
                type="button"
                onClick={leaveAndClose}
                data-testid="floating-leave-btn"
                title="Salir de la clase"
                className="text-white bg-[#A52258] hover:bg-[#8A1C49] rounded-full p-1.5"
              >
                <LogOut size={13} />
              </button>
            )}
          </div>
        </div>
      )}
      <div style={{ width: "100%", height: "100%", paddingTop: floating ? 32 : 0 }}>
        <AgoraRoom
          channelName={data?.embed_room_name || classId}
          classId={classId}
          onLeave={leaveAndClose}
          isRecording={!!data?.is_recording}
        />
      </div>
    </div>
  );
}
